import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Link as RouterLink } from 'react-router-dom';
import Loader from '@clearscore/rainbow.loader';
import Text from '@clearscore/rainbow.text';
import toTitleCase from '@clearscore/normalize.title-case';

import { types, sizes, themes } from './constants';
import styles from './button.css';

export default class Button extends PureComponent {
    static types = types;
    static sizes = sizes;
    static themes = themes;

    get isDisabled() {
        const { isDisabled, isLoading } = this.props;
        return isDisabled || isLoading;
    }

    handleClick = (e) => {
        const { onClick } = this.props;

        if (!this.isDisabled && onClick) {
            onClick(e);
        }
    };

    get target() {
        const { href, newTab } = this.props;
        if (!href) {
            return null;
        }

        return newTab ? '_blank' : '_self';
    }

    getButtonText = () => {
        const { isLoading, size, children } = this.props;
        const sizeClass = styles[`isSize${toTitleCase(size)}`];
        const loaderClass = cx(styles.loaderWrapper, sizeClass);
        const TextButton = size === sizes.LARGE ? Text.Button1 : Text.Button2;
        return (
            <TextButton>
                {isLoading ? (
                    <div className={loaderClass}>
                        <div className={styles.loader}>
                            <Loader size={Loader.sizes[size]} />
                        </div>
                        {'\u00A0'}
                    </div>
                ) : (
                    children
                )}
            </TextButton>
        );
    };

    render() {
        const {
            children,
            type,
            size,
            theme,
            isWide,
            isNarrow,
            isFlat,
            isResponsive,
            isDisabled,
            isLoading,
            href,
            dataId,
            htmlType,
            newTab,
            routerTrackingSource,
            routerTrackingProps,
            state,
            ...props
        } = this.props;
        const typeClass = styles[`isType${toTitleCase(type)}`];
        const sizeClass = styles[`isSize${toTitleCase(size)}`];
        const themeClass = styles[`isTheme${toTitleCase(theme)}`];
        const isInternalLink = !newTab && href && !href.startsWith('http');
        const Tag = href ? 'a' : 'button';
        const buttonProps = {
            className: cx(styles.component, typeClass, sizeClass, themeClass, {
                [styles.isWide]: isWide,
                [styles.isNarrow]: isNarrow,
                [styles.isFlat]: isFlat,
                [styles.isDisabled]: isDisabled,
                [styles.isResponsive]: isResponsive,
            }),
            disabled: this.isDisabled,
            onClick: this.handleClick,
        };

        if (isInternalLink) {
            const hasState = state || routerTrackingSource || routerTrackingProps;

            const getToPropValues = () => {
                if (state && (routerTrackingSource || routerTrackingProps)) {
                    return {
                        pathname: href,
                        state: {
                            ...state,
                            tracking: {
                                source: routerTrackingSource,
                                props: routerTrackingProps,
                            },
                        },
                    };
                }

                if (state) {
                    return {
                        pathname: href,
                        state,
                    };
                }

                return {
                    pathname: href,
                    state: {
                        tracking: {
                            source: routerTrackingSource,
                            props: routerTrackingProps,
                        },
                    },
                };
            };

            const to = hasState ? getToPropValues() : href;

            return (
                <RouterLink {...buttonProps} {...props} data-id={dataId} data-qa="button" to={to}>
                    {this.getButtonText()}
                </RouterLink>
            );
        }

        return (
            <Tag
                {...props}
                {...buttonProps}
                href={href || undefined}
                type={href ? '' : htmlType}
                target={this.target}
                rel={this.target && 'noopener noreferrer'}
                data-qa="button"
                data-id={dataId}
            >
                {this.getButtonText()}
            </Tag>
        );
    }
}

Button.propTypes = {
    children: PropTypes.node.isRequired,
    type: PropTypes.oneOf(Object.values(types)),
    size: PropTypes.oneOf(Object.values(sizes)),
    theme: PropTypes.oneOf(Object.values(themes)),
    isWide: PropTypes.bool,
    isNarrow: PropTypes.bool,
    isFlat: PropTypes.bool,
    isResponsive: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    onClick: PropTypes.func,
    href: PropTypes.string,
    newTab: PropTypes.bool,
    htmlType: PropTypes.string,
    dataId: PropTypes.string,
    routerTrackingSource: PropTypes.string,
    routerTrackingProps: PropTypes.object,
    state: PropTypes.object,
};

Button.defaultProps = {
    type: types.PRIMARY,
    size: sizes.SMALL,
    theme: themes.LIGHT,
    isWide: false,
    isNarrow: false,
    isFlat: false,
    isResponsive: false,
    isDisabled: false,
    isLoading: false,
    onClick: null,
    href: null,
    newTab: false,
    htmlType: 'button',
    dataId: undefined,
    routerTrackingSource: undefined,
    routerTrackingProps: undefined,
    state: undefined,
};
